'use es6';

import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import partial from 'transmute/partial';
import { COMPANY_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import PhoneNumberOption from './PhoneNumberOption';
import AddNumberToCallee from './AddNumberToCallee';
import CalleeObjectGroupOption from './CalleeObjectGroupOption';
import { createPropertyKey } from 'calling-lifecycle-internal/callees/operators/getPropertyKeys';
import { getPhoneNumberProperties, getObjectTypeId, getObjectId, getValue, getCanCommunicate } from 'calling-lifecycle-internal/callees/operators/calleesOperators';
import { getAdditionalProperties } from 'calling-lifecycle-internal/callees/operators/calleesOperators';
import { getFormattedName } from 'calling-lifecycle-internal/callees/operators/propertyValueGetters';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
function CalleeNumberOptions({
  appIdentifier,
  editPermissionsResults = null,
  callableObject,
  onEditProperty,
  onSelectToNumber,
  disabled,
  OptionIcon,
  onAddPropertyClick,
  showCalleeNames,
  disabledTooltipMessage,
  showAddPropertyButton
}) {
  const callableOptions = getPhoneNumberProperties(callableObject);
  const optionText = `getText(option)`;
  const objectId = getObjectId(callableObject);
  const objectTypeId = getObjectTypeId(callableObject);
  const formattedName = getFormattedName(getAdditionalProperties(callableObject));
  const cannotCommunicate = !getCanCommunicate(callableObject);
  const cannotCommunicateMessage = cannotCommunicate ? 'callSettingsOptions.cannotCommunicate' : '';
  const canAddNumber = showAddPropertyButton && (objectTypeId !== COMPANY_TYPE_ID || objectTypeId === COMPANY_TYPE_ID && !getValue(callableOptions.first()));
  let editPermissions;
  if (editPermissionsResults) {
    const propertyKey = createPropertyKey({
      objectId,
      objectTypeId
    });
    editPermissions = editPermissionsResults.get(propertyKey);
  }
  const options = useMemo(() => callableOptions.reduce((array, phoneNumberProperty, key) => {
    const groupedOptionValue = getValue(phoneNumberProperty);
    if (!groupedOptionValue) {
      return array;
    }
    const phoneNumberIndex = callableOptions.keySeq().indexOf(key);
    const OptionComponent =
    /*#__PURE__*/
    // leave div to fix unstyled button margins
    _jsx("div", {
      children: /*#__PURE__*/_jsx(PhoneNumberOption, {
        appIdentifier: appIdentifier,
        phoneNumberProperty: phoneNumberProperty,
        onSelect: onSelectToNumber,
        selectToNumberIndex: phoneNumberIndex,
        callableObject: callableObject,
        editPermissions: editPermissions,
        onEditProperty: onEditProperty,
        disabled: disabled || cannotCommunicate,
        OptionIcon: OptionIcon,
        disabledTooltipMessage: disabledTooltipMessage || cannotCommunicateMessage,
        formattedName: formattedName,
        objectTypeId: objectTypeId
      }, `${callableObject.hashCode()}__${phoneNumberProperty.hashCode()}`)
    });
    array.push(OptionComponent);
    return array;
  }, []), [callableOptions, callableObject, appIdentifier, onSelectToNumber, editPermissions, onEditProperty, disabled, cannotCommunicate, OptionIcon, disabledTooltipMessage, cannotCommunicateMessage, formattedName, objectTypeId]);
  const calleeName = showCalleeNames ? /*#__PURE__*/_jsx(View, {
    marginBlockStart: "100",
    as: "div",
    children: /*#__PURE__*/_jsx(CalleeObjectGroupOption, {
      callableObject: callableObject,
      objectTypeId: objectTypeId
    })
  }, callableObject.hashCode()) : null;
  const addNumberLink = canAddNumber ? /*#__PURE__*/_jsx(AddNumberToCallee, {
    onClick: partial(onAddPropertyClick, {
      callableObject
    }),
    editPermissions: editPermissions
  }, `${optionText}_addNumber`) : null;
  return [calleeName, options, addNumberLink];
}
CalleeNumberOptions.propTypes = {
  appIdentifier: PropTypes.string.isRequired,
  onEditProperty: PropTypes.func,
  onSelectToNumber: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  editPermissionsResults: ImmutablePropTypes.map,
  options: ImmutablePropTypes.list,
  OptionIcon: PropTypes.object,
  onAddPropertyClick: PropTypes.func,
  showCalleeNames: PropTypes.bool.isRequired,
  disabledTooltipMessage: PropTypes.string,
  showAddPropertyButton: PropTypes.bool.isRequired
};
export default /*#__PURE__*/memo(CalleeNumberOptions);